<template>
  <AppClientSchoolLayout title="成績">
    <div class=" max-w-7xl mx-auto py-6">
      <div class="grid grid-cols-8 flex-col">
        <div class="col-span-2 m-4">
          <div class="shadow-lg bg-white mr-1 border border-gray-200 rounded-lg">
            <div class="p-4">
              <div class="uppercase tracking-wide text-c2 mb-4">
                模試一覧
              </div>
              <div
                v-for="(examination, i) in examinations"
                :key="i"
                :class="{
                  'bg-indigo-500': selectedExamination.id === examination.id,
                  'text-white': selectedExamination.id === examination.id,
                }">
                <button
                  class="w-full"
                  @click="selectExamination(examination.id)">
                  <div
                    class="flex cursor-pointer border py-2 text-lg text-left text-grey-darkest hover:bg-indigo-500 hover:text-white">
                    <div class="pl-2 pt-1 text-sm">
                      {{ examination.title }}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-6 bg-gray-100 my-4 mr-4 shadow overflow-hidden sm:rounded-lg">
          <div class="bg-white px-4 py-5 sm:px-6">
            <h4 class="text-lg leading-6 font-medium text-gray-900">
              学生成績
            </h4>
          </div>
          <div class="border-t border-gray-200">
            <dl>
              <div class="bg-white px-4 py-5">
                <dt class="text-md font-medium text-gray-500">
                  模試名
                </dt>
                <dd class="mt-2 ml-4 text-md text-gray-900">
                  {{ selectedExamination.title }}
                </dd>
              </div>
              <div
                v-if="users.data.length"
                class="">
                <div class="mt-4 ml-6 text-sm">
                  <p>成績データCSV出力はリストに表示されている学生のデータが出力されます。</p>
                  <p>全データを出力する場合はPerPageをALLに設定してください。</p>
                </div>
                <div>
                  <button
                    :disabled="!users.data.length"
                    class="mt-4 mx-6 py-3 px-2 bg-green-600 hover:shadow-lg text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed">
                    <!-- TODO: バックエンド側でCSV出力処理を行う実装への変更 -->
                    <download-csv
                      :data="getCsvResultData"
                      :labels="csvResultLabels"
                      :name="csvResultFileName">
                      成績データCSV出力
                    </download-csv>
                  </button>
                  <button
                    :disabled="!schoolQuestionCorrentAnswerRate.length"
                    class="mt-4 py-3 px-2 bg-green-600 hover:shadow-lg text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed">
                    <download-csv
                      :data="getCsvAnalyticsData"
                      :labels="csvAnalyticsLabels"
                      :name="csvAnalyticsFileName">
                      問題別正答率データCSV出力
                    </download-csv>
                  </button>
                </div>
              </div>
              <div class="pt-8">
                <div class="max-w-7xl mx-auto ">
                  <div>
                    <ul class="flex">
                      <li
                        :class="currentShowTabDisplay === 0 ? 'bg-white' : 'bg-gray-300'"
                        class="cursor-pointer p-5 ml-4 mx-2 rounded-t-xl"
                        @click="changeTab(0)">
                        成績一覧
                      </li>
                      <li
                        :class="currentShowTabDisplay === 1 ? 'bg-white' : 'bg-gray-300'"
                        class="cursor-pointer p-5 mx-2 rounded-t-xl"
                        @click="changeTab(1)">
                        分析
                      </li>
                    </ul>
                  </div>
                  <div class="p-5 bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <div>
                      <ul>
                        <li v-if="currentShowTabDisplay === 0">
                          <StudentResultList
                            :users="users"
                            :selected-examination="selectedExamination"
                            :limit="limit"
                            :sort="sort"
                            :order="order"
                            :user-id="userId"
                            :student-id="studentId"
                            :service-years="serviceYears"
                            :name-keyword="nameKeyword"
                            :rankings="rankings" />
                        </li>
                        <li v-if="currentShowTabDisplay === 1">
                          <analytics
                            :users="users"
                            :questions="questions"
                            :examinations="examinations"
                            :examination-types="examinationTypes"
                            :subject-names="subjectNames"
                            :selected-examination="selectedExamination"
                            :question-corrent-answer-rate="questionCorrentAnswerRate"
                            :subject-corrent-answer-rate="subjectCorrentAnswerRate"
                            :examination-score="examinationScore"
                            :examinees-count="examineesCount"
                            :school-examination-score="schoolExaminationScore"
                            :school-subject-corrent-answer-rate="schoolSubjectCorrentAnswerRate"
                            :school-question-corrent-answer-rate="schoolQuestionCorrentAnswerRate"
                            :school-examinees-count="schoolExamineesCount"
                            :school-total-low-score="schoolTotalLowScore"
                            :school-total-highest-score="schoolTotalHighestScore"
                            :analytics-service-years="analyticsServiceYears" />
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </AppClientSchoolLayout>
</template>

<script setup>
import moment from 'moment'
import { Inertia } from '@inertiajs/inertia'
import {
  reactive, ref, onMounted, computed,
} from 'vue'
import DownloadCsv from 'vue-json-csv'
import DataTable from '@/Common/DataTable'
import Pagination from '../../../Common/Pagination'
import convertToGrade from '@/actions/serviceYears/convert'
import AppClientSchoolLayout from '@/Layouts/AppClientSchoolLayout'
import Analytics from './Analytics'
import StudentResultList from './StudentResultList'

const props = defineProps({
  users: {
    type: Object,
    default: () => ({}),
  },
  questions: {
    type: Array,
    default: () => [],
  },
  examinations: {
    type: Array,
    default: () => [],
  },
  examinationTypes: {
    type: Object,
    default: () => ({}),
  },
  subjectNames: {
    type: Array,
    default: () => [],
  },
  selectedExamination: {
    type: Object,
    default: () => ({}),
  },
  limit: {
    type: String,
    default: '15',
  },
  sort: {
    type: String,
    default: 'id',
  },
  order: {
    type: String,
    default: 'asc',
  },
  userId: {
    type: String,
    default: null,
  },
  studentId: {
    type: String,
    default: null,
  },
  serviceYears: {
    type: String,
    default: null,
  },
  nameKeyword: {
    type: String,
    default: null,
  },
  examinationScore: {
    type: Array,
    default: () => [],
  },
  subjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  questionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  examineesCount: {
    type: Number,
    default: 0,
  },
  schoolExaminationScore: {
    type: Array,
    default: () => [],
  },
  schoolSubjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  schoolQuestionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  schoolExamineesCount: {
    type: Number,
    default: 0,
  },
  schoolTotalLowScore: {
    type: Number,
    default: 0,
  },
  schoolTotalHighestScore: {
    type: Number,
    default: 0,
  },
  totalLowScore: {
    type: Number,
    default: 0,
  },
  totalHighestScore: {
    type: Number,
    default: 0,
  },
  rankings: {
    type: Array,
    default: () => [],
  },
  analyticsServiceYears: {
    type: Number,
    default: 0,
  },
  showTabDisplay: {
    type: Number,
    default: 0,
  },
})

const csvResultLabels = (['学籍番号', '学年', '名前', '校内順位', '全国順位', '必須', '一般', '実地', '合計'])
const csvResultFileName = `resultData_${String(props.selectedExamination.id)}_${String(props.selectedExamination.title)}_${String(moment().format('YYYYMMDD'))}.csv`
const csvAnalyticsLabels = (['種別', '問題番号', '科目', '校内正答率', '全国正答率', '問題文'])
const csvAnalyticsFileName = `analyticsData_${String(props.selectedExamination.id)}_${String(props.selectedExamination.title)}_${String(moment().format('YYYYMMDD'))}.csv`

const currentShowTabDisplay = ref(props.showTabDisplay) ?? 0
const changeTab = (num) => {
  currentShowTabDisplay.value = num
}

const selectExamination = (id) => {
  Inertia.get(route('school.result', {
    examinationId: id,
  }))
}

const ExaminationType = (examinationType) => props.examinationTypes[examinationType]
const subjectName = (subjectId) => props.subjectNames.find(item => item.id === subjectId).name

const getCorrectAnswerRate = (questionId) => {
  const question = props.questionCorrentAnswerRate.find(value => value.question_id === questionId)

  if (question) {
    return question.correct_answer_rate
  }
  return '-'
}

const getQuestion = (questionId) => {
  const question = props.questions.find(value => value.question_id === questionId)

  if (question) {
    return question.question
  }
  return '-'
}

const getCsvResultData = computed(() => {
  const csvData = []
  props.users.data.forEach(element => {
    const userGrade = convertToGrade(element.service_years)
    const schoolRanking = element.school_ranking ?? '-'
    const ranking = props.rankings.find(value => value.user_id === element.client_id)
    const compulsory = !Number.isNaN(element.compulsory) ? element.compulsory : '-'
    const general = element.general ?? '-'
    const practice = !Number.isNaN(element.practice) ? element.practice : '-'

    const compulsoryScore = !Number.isNaN(element.compulsory) ? element.compulsory : 0
    const generalScore = !Number.isNaN(element.general) ? element.general : 0
    const practiceScore = !Number.isNaN(element.practice) ? element.practice : 0
    const total = compulsoryScore + generalScore + practiceScore

    csvData.push([
      element.student_id,
      userGrade,
      element.name,
      schoolRanking,
      ranking ? ranking.ranking : '-',
      compulsory,
      general,
      practice,
      total,
    ])
  })
  return csvData
})

const getCsvAnalyticsData = computed(() => {
  const csvData = []
  props.schoolQuestionCorrentAnswerRate.forEach(element => {
    const examinationType = ExaminationType(element.examination_type)
    const subject = subjectName(element.subject_id)
    const correctAnswerRate = getCorrectAnswerRate(element.question_id)
    const question = getQuestion(element.question_id)

    csvData.push([
      examinationType,
      element.sort_index + 1,
      subject,
      element.correct_answer_rate,
      correctAnswerRate,
      question,
    ])
  })
  return csvData
})

onMounted(() => {
  changeTab(currentShowTabDisplay.value ?? 0)
})
</script>

<style></style>
